/**
 * ArtefactPreviewModal — show the built table and let the writer approve it.
 *
 * The build endpoint is preview-only; nothing is saved until the writer
 * clicks "Approve & save" here. On approve the shell uploads the exact CSV
 * (content_type text/csv) into the source library and refreshes the inventory.
 * Cancel discards the preview and writes nothing.
 *
 * Pure presentation — the shell owns the API calls and open/close.
 */

import { makeStyles, mergeClasses } from "@fluentui/react-components";
import type React from "react";
import { useState } from "react";
import type { TableArtefactPreview } from "../../../api/client";
import { brandColors, fonts, inkColors, surfaceColors } from "../../tokens";

const useStyles = makeStyles({
  backdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(15, 10, 30, 0.55)",
    zIndex: 1001,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px",
    fontFamily: fonts.body,
  },
  dialog: {
    background: surfaceColors.paper,
    border: `1px solid ${surfaceColors.lineStrong}`,
    borderTop: `3px solid ${brandColors.primary}`,
    borderRadius: "4px",
    width: "100%",
    maxWidth: "560px",
    maxHeight: "84vh",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 20px 48px rgba(15, 10, 30, 0.25)",
  },
  header: { padding: "14px 16px 8px" },
  title: { fontFamily: fonts.display, fontSize: "15px", fontWeight: 700, color: inkColors.ink },
  meta: { fontSize: "10px", fontFamily: fonts.mono, color: inkColors.ink4, marginTop: "3px" },
  filenameRow: { display: "flex", alignItems: "center", columnGap: "6px", padding: "6px 16px 10px" },
  filenameLabel: { fontSize: "10px", fontFamily: fonts.mono, color: inkColors.ink3, textTransform: "uppercase", letterSpacing: "0.5px" },
  filenameInput: {
    flex: 1,
    border: `1px solid ${surfaceColors.line}`,
    borderRadius: "3px",
    padding: "5px 7px",
    fontSize: "11px",
    fontFamily: fonts.mono,
    color: inkColors.ink,
    outline: "none",
  },
  tableWrap: {
    margin: "0 16px",
    border: `1px solid ${surfaceColors.line}`,
    borderRadius: "4px",
    overflow: "auto",
    flex: 1,
    minHeight: 0,
  },
  table: { borderCollapse: "collapse", width: "100%", fontSize: "10.5px", fontFamily: fonts.mono },
  th: {
    position: "sticky",
    top: 0,
    background: surfaceColors.paperDim,
    borderBottom: `1px solid ${surfaceColors.lineStrong}`,
    borderRight: `1px solid ${surfaceColors.line}`,
    padding: "5px 8px",
    textAlign: "left",
    fontWeight: 700,
    color: inkColors.ink2,
    whiteSpace: "nowrap",
  },
  td: {
    borderBottom: `1px solid ${surfaceColors.line}`,
    borderRight: `1px solid ${surfaceColors.line}`,
    padding: "4px 8px",
    color: inkColors.ink,
    whiteSpace: "nowrap",
  },
  truncated: { padding: "6px 16px", fontSize: "10px", color: inkColors.ink4, fontStyle: "italic" },
  error: { padding: "0 16px", fontSize: "10.5px", color: "#b42318" },
  actions: { display: "flex", gap: "8px", padding: "12px 16px" },
  btn: {
    flex: 1,
    fontSize: "12px",
    fontWeight: 700,
    padding: "9px 14px",
    borderRadius: "3px",
    cursor: "pointer",
    fontFamily: fonts.body,
    letterSpacing: "0.3px",
    textTransform: "uppercase",
    border: "none",
  },
  btnPrimary: { background: brandColors.primary, color: "#fff" },
  btnSecondary: { background: "#fff", color: inkColors.ink2, border: `1px solid ${surfaceColors.lineStrong}` },
  btnDisabled: { opacity: 0.5, cursor: "not-allowed" },
});

export interface ArtefactPreviewModalProps {
  preview: TableArtefactPreview;
  /** True while the approve→save upload is in flight. */
  saving?: boolean;
  saveError?: string | null;
  onCancel: () => void;
  /** Writer approved — persist the CSV under this filename. */
  onApprove: (filename: string) => void;
}

export const ArtefactPreviewModal: React.FC<ArtefactPreviewModalProps> = ({
  preview,
  saving = false,
  saveError = null,
  onCancel,
  onApprove,
}) => {
  const styles = useStyles();
  const [filename, setFilename] = useState(preview.suggested_filename || "table_artefact.csv");

  return (
    <dialog open className={styles.backdrop} aria-modal="true" aria-labelledby="artefact-preview-title">
      <div className={styles.dialog}>
        <div className={styles.header}>
          <div id="artefact-preview-title" className={styles.title}>
            Preview artefact
          </div>
          <div className={styles.meta}>
            {preview.columns.length} columns · {preview.row_count} rows
          </div>
        </div>

        <div className={styles.filenameRow}>
          <span className={styles.filenameLabel}>Save as</span>
          <input
            className={styles.filenameInput}
            value={filename}
            onChange={(e) => setFilename(e.target.value)}
            aria-label="Filename"
          />
        </div>

        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                {preview.columns.map((c) => (
                  <th key={c} className={styles.th}>
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {preview.preview_rows.map((row, ri) => (
                // rows have no stable id; index is fine for a read-only preview
                // biome-ignore lint/suspicious/noArrayIndexKey: static preview
                <tr key={ri}>
                  {row.map((cell, ci) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: static preview
                    <td key={ci} className={styles.td}>
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {preview.truncated_preview && (
          <div className={styles.truncated}>
            Showing the first {preview.preview_rows.length} of {preview.row_count} rows. The saved CSV contains all rows.
          </div>
        )}
        {saveError && <div className={styles.error}>{saveError}</div>}

        <div className={styles.actions}>
          <button
            type="button"
            className={mergeClasses(styles.btn, styles.btnSecondary)}
            onClick={onCancel}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            className={mergeClasses(styles.btn, styles.btnPrimary, (saving || !filename.trim()) && styles.btnDisabled)}
            onClick={() => filename.trim() && onApprove(filename.trim())}
            disabled={saving || !filename.trim()}
            data-testid="artefact-approve-save"
          >
            {saving ? "Saving…" : "Approve & save"}
          </button>
        </div>
      </div>
    </dialog>
  );
};

export default ArtefactPreviewModal;
